import { usDate } from "./dateText.ts";
// LOC2-S3. The tenant Settings form remains the customer's default. This
// panel edits only explicit overrides for a selected location, one field at a time.
import {
  getLocationMoneyConfirmation,
  getLocationSettings,
  getPlanningLocations,
  getTaxRates,
  postLocationMoneyConfirmation,
  putLocationSetting,
  putTaxRates,
  revertLocationSetting,
  type LocationSettingKey,
  type LocationSettingsDto,
  type PlanningLocationDto,
} from "./api.ts";
import { fillDiscountApprovalSelect } from "./discountApprovalPicks.ts";
import { hasPermission, type Session } from "./session.ts";
import { todayInZone } from "./taxRateSettingsModel.ts";
import { fillZoneOptions } from "./timeZoneOptions.ts";

const panel = document.querySelector<HTMLElement>("#admin-location-settings");
const locationSelect = document.querySelector<HTMLSelectElement>(
  "#admin-location-settings-location",
);
const fields = document.querySelector<HTMLElement>("#admin-location-settings-fields");
const status = document.querySelector<HTMLElement>("#admin-location-settings-status");
if (panel === null || locationSelect === null || fields === null || status === null) {
  throw new Error("location settings panel is missing");
}
const panelEl = panel;
const locationSelectEl = locationSelect;
const fieldsEl = fields;
const statusEl = status;

type FieldKind =
  | "model"
  | "ounces"
  | "dollars"
  | "time"
  | "timezone"
  | "percentages"
  | "approval_percent"
  | "approval_dollars"
  | "yes_no"
  | "dual_percent"
  | "yes_no_tip_basis";
const FIELD_DEFS: ReadonlyArray<{ key: LocationSettingKey; label: string; kind: FieldKind }> = [
  { key: "pricing_model", label: "Pricing model", kind: "model" },
  { key: "standard_pour_ounces", label: "Standard pour (oz)", kind: "ounces" },
  { key: "hold_amount_cents", label: "Hold amount ($, blank for no hold)", kind: "dollars" },
  {
    key: "purchase_approval_threshold_cents",
    label: "Purchase approval threshold ($)",
    kind: "dollars",
  },
  {
    key: "discount_approval_percent_basis_points",
    label: "Discount needs a manager above this share off",
    kind: "approval_percent",
  },
  {
    key: "discount_approval_amount_cents",
    label: "Discount needs a manager above this amount off",
    kind: "approval_dollars",
  },
  {
    key: "close_card_tickets_at_day_end",
    label: "Close card tickets at the end of the day",
    kind: "yes_no",
  },
  {
    key: "dual_pricing_enabled",
    label: "Dual pricing (card price above cash price)",
    kind: "yes_no",
  },
  { key: "dual_pricing_basis_points", label: "Card price difference", kind: "dual_percent" },
  {
    key: "tip_on_pre_discount_price",
    label: "Suggest tips on the price before discounts",
    kind: "yes_no_tip_basis",
  },
  { key: "business_day_cutoff_time_local", label: "Business day cutoff", kind: "time" },
  { key: "business_day_timezone", label: "Business day time zone", kind: "timezone" },
  {
    key: "tip_presets_basis_points",
    label: "Tip choices (%, separated by commas)",
    kind: "percentages",
  },
];

let generation = 0;
let activeSession: Session | undefined;
let locations: PlanningLocationDto[] = [];
let currentSettings: LocationSettingsDto | undefined;

function errorMessage(body: { message?: string }): string {
  return body.message ?? "Check your connection and try again.";
}

function valueFor(settings: LocationSettingsDto, key: LocationSettingKey): unknown {
  switch (key) {
    case "pricing_model":
      return settings.pricingModel;
    case "standard_pour_ounces":
      return settings.standardPourOunces;
    case "hold_amount_cents":
      return settings.holdAmountCents;
    case "purchase_approval_threshold_cents":
      return settings.purchaseApprovalThresholdCents;
    case "discount_approval_percent_basis_points":
      return settings.discountApprovalPercentBasisPoints;
    case "discount_approval_amount_cents":
      return settings.discountApprovalAmountCents;
    case "close_card_tickets_at_day_end":
      return settings.closeCardTicketsAtDayEnd;
    case "dual_pricing_enabled":
      return settings.dualPricingEnabled;
    case "dual_pricing_basis_points":
      return settings.dualPricingBasisPoints;
    case "tip_on_pre_discount_price":
      return settings.tipOnPreDiscountPrice;
    case "business_day_cutoff_time_local":
      return settings.businessDayCutoffTimeLocal;
    case "business_day_timezone":
      return settings.businessDayTimezone;
    case "tip_presets_basis_points":
      return settings.tipPresetsBasisPoints;
  }
}

function dollars(cents: number | null): string {
  return cents === null ? "" : `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
}

function percent(basisPoints: number): string {
  return `${Math.floor(basisPoints / 100)}.${String(basisPoints % 100).padStart(2, "0")}`;
}

function exactHundredths(raw: string, label: string): number {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(raw.trim());
  if (match === null) throw new Error(`${label} needs a number with at most two decimal places.`);
  const amount = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  if (!Number.isSafeInteger(amount)) throw new Error(`${label} is too large.`);
  return amount;
}

function readValue(key: LocationSettingKey, input: HTMLInputElement | HTMLSelectElement): unknown {
  const raw = input.value.trim();
  switch (key) {
    case "hold_amount_cents":
      return raw === "" ? null : exactHundredths(raw, "Hold amount");
    case "purchase_approval_threshold_cents":
      return exactHundredths(raw, "Approval threshold");
    case "discount_approval_percent_basis_points":
    case "discount_approval_amount_cents": {
      const picked = Number(raw);
      if (!Number.isInteger(picked) || picked <= 0) throw new Error("Pick a value from the list.");
      return picked;
    }
    case "close_card_tickets_at_day_end":
    case "dual_pricing_enabled":
    case "tip_on_pre_discount_price":
      return raw === "true";
    case "dual_pricing_basis_points": {
      // Two decimals, 0.00 to 4.00, picked from a list so a typo cannot reach the server.
      const picked = Number(raw);
      if (!Number.isInteger(picked) || picked < 0 || picked > 400) throw new Error("Pick a value from the list.");
      return picked;
    }
    case "tip_presets_basis_points":
      return raw === ""
        ? []
        : raw.split(",").map((part) => {
            const hundredths = exactHundredths(part, "Tip percentage");
            if (hundredths % 100 !== 0)
              throw new Error("Tip choices must be whole percents, such as 20 or 22. The card reader cannot show 22.5%.");
            return hundredths;
          });
    default:
      if (raw === "") throw new Error("Choose a value before saving this override.");
      return raw;
  }
}

function inputFor(
  def: (typeof FIELD_DEFS)[number],
  value: unknown,
): HTMLInputElement | HTMLSelectElement {
  if (def.kind === "model" || def.kind === "timezone") {
    const select = document.createElement("select");
    select.className = "field-input";
    if (def.kind === "timezone") {
      // UX133: the shared grouped, offset-labelled zone list; the option value stays the IANA id.
      const zones =
        typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
      fillZoneOptions(select, [...zones, "UTC"], String(value));
      return select;
    }
    for (const optionValue of ["per_ounce", "shelf"]) {
      const option = document.createElement("option");
      option.value = optionValue;
      option.textContent = optionValue === "shelf" ? "Shelf" : "Per ounce";
      select.append(option);
    }
    select.value = String(value);
    return select;
  }
  if (def.kind === "dual_percent") {
    const select = document.createElement("select");
    select.className = "field-input";
    const current = typeof value === "number" ? value : 270;
    for (let basisPoints = 0; basisPoints <= 400; basisPoints += 1) {
      // Every 0.05%, plus whatever the customer value is, so an off-grid value still shows as itself.
      if (basisPoints % 5 !== 0 && basisPoints !== current) continue;
      const option = document.createElement("option");
      option.value = String(basisPoints);
      option.textContent = `${(basisPoints / 100).toFixed(2)}%`;
      select.append(option);
    }
    select.value = String(typeof value === "number" ? value : 270);
    return select;
  }
  if (def.kind === "yes_no") {
    const select = document.createElement("select");
    select.className = "field-input";
    const yesNoOptions: ReadonlyArray<readonly [string, string]> =
      def.key === "dual_pricing_enabled"
        ? [
            ["false", "Off"],
            ["true", "On"],
          ]
        : [
            ["true", "Close them"],
            ["false", "Leave them open"],
          ];
    for (const [optionValue, text] of yesNoOptions) {
      const option = document.createElement("option");
      option.value = optionValue;
      option.textContent = text;
      select.append(option);
    }
    select.value = value === false ? "false" : "true";
    return select;
  }
  if (def.kind === "yes_no_tip_basis") {
    const select = document.createElement("select");
    select.className = "field-input";
    for (const [optionValue, text] of [
      ["false", "On the discounted price"],
      ["true", "On the price before discounts"],
    ] as const) {
      const option = document.createElement("option");
      option.value = optionValue;
      option.textContent = text;
      select.append(option);
    }
    select.value = value === true ? "true" : "false";
    return select;
  }
  if (def.kind === "approval_percent" || def.kind === "approval_dollars") {
    const select = document.createElement("select");
    select.className = "field-input";
    fillDiscountApprovalSelect(
      select,
      def.kind === "approval_percent" ? "percent" : "dollars",
      Number(value),
    );
    return select;
  }
  const input = document.createElement("input");
  input.className = "field-input";
  input.type = def.kind === "time" ? "time" : def.kind === "percentages" ? "text" : "number";
  if (input.type === "number") {
    input.inputMode = "decimal";
    input.step = "0.01";
    input.min = "0";
  }
  input.value =
    def.kind === "dollars"
      ? dollars(value as number | null)
      : def.kind === "percentages"
        ? (value as number[]).map((bps) => (bps % 100 === 0 ? String(bps / 100) : percent(bps))).join(", ")
        : String(value);
  return input;
}

function render(settings: LocationSettingsDto, gen: number): void {
  const session = activeSession;
  if (session === undefined || gen !== generation) return;
  currentSettings = settings;
  fieldsEl.replaceChildren();
  const canManage = hasPermission(session, "admin.location_settings.manage");
  for (const def of FIELD_DEFS) {
    const row = document.createElement("div");
    row.className = "bd-card";
    const label = document.createElement("label");
    label.className = "field-label";
    label.textContent = def.label;
    const input = inputFor(def, valueFor(settings, def.key));
    input.dataset["testid"] = `admin-location-setting-${def.key}`;
    input.disabled = !canManage;
    label.append(input);
    const state = document.createElement("p");
    state.className = "bd-help";
    state.textContent = settings.overriddenKeys.includes(def.key)
      ? "Override for this location"
      : "Inherits the customer's value";
    row.append(label, state);
    if (canManage) {
      const actions = document.createElement("div");
      actions.className = "bd-actions";
      const save = document.createElement("button");
      save.type = "button";
      save.className = "btn-secondary btn-compact";
      save.textContent = "Save override";
      save.dataset["testid"] = `admin-location-setting-save-${def.key}`;
      save.addEventListener("click", () => void write(def.key, input, false));
      const revert = document.createElement("button");
      revert.type = "button";
      revert.className = "btn-secondary btn-compact";
      revert.textContent = "Use customer value";
      revert.disabled = !settings.overriddenKeys.includes(def.key);
      revert.dataset["testid"] = `admin-location-setting-revert-${def.key}`;
      revert.addEventListener("click", () => void write(def.key, input, true));
      actions.append(save, revert);
      row.append(actions);
    }
    fieldsEl.append(row);
  }
  void loadMoneyConfirmations(settings, gen);
}

async function loadMoneyConfirmations(settings: LocationSettingsDto, gen: number): Promise<void> {
  const session = activeSession;
  const locationId = locationSelectEl.value;
  if (session === undefined || locationId === "") return;
  for (const kind of ["business_day", "tax_rate"] as const) {
    const permission =
      kind === "tax_rate" ? "admin.tax_rate.manage" : "admin.location_settings.manage";
    if (!hasPermission(session, permission)) continue;
    const result = await getLocationMoneyConfirmation(
      session.apiBaseUrl,
      session.tenantId,
      locationId,
      kind,
    );
    if (gen !== generation || locationId !== locationSelectEl.value) return;
    const card = document.createElement("div");
    card.className = "bd-card";
    const title = document.createElement("h4");
    title.textContent =
      kind === "tax_rate" ? "This location's sales tax" : "This location's business day";
    card.append(title);
    if (result.status !== 200) {
      const problem = document.createElement("p");
      problem.className = "bd-help";
      problem.textContent = `Could not load confirmation. ${errorMessage(result.body)}`;
      card.append(problem);
      fieldsEl.append(card);
      continue;
    }
    const facts = result.body;
    const detail = document.createElement("p");
    detail.className = "bd-help";
    detail.textContent =
      kind === "tax_rate"
        ? `${percent(facts.display.basisPoints ?? settings.taxRateBasisPoints)}%${facts.display.effectiveFrom === undefined ? " inherited" : ` from ${usDate(facts.display.effectiveFrom)}`}. ${facts.confirmed ? "Confirmed." : "Needs confirmation before live card charges."}`
        : `${facts.display.timezone ?? settings.businessDayTimezone}, cutoff ${facts.display.cutoff ?? settings.businessDayCutoffTimeLocal}. ${facts.confirmed ? "Confirmed." : "Needs confirmation before live card charges."}`;
    card.append(detail);
    if (kind === "tax_rate") {
      const rateLabel = document.createElement("label");
      rateLabel.className = "field-label";
      rateLabel.textContent = "New combined sales tax rate (%)";
      const rateInput = document.createElement("input");
      rateInput.className = "field-input";
      rateInput.inputMode = "decimal";
      rateInput.value = percent(facts.display.basisPoints ?? settings.taxRateBasisPoints);
      rateInput.dataset["testid"] = "admin-location-tax-rate";
      rateLabel.append(rateInput);
      const dateLabel = document.createElement("label");
      dateLabel.className = "field-label";
      dateLabel.textContent = "Effective date";
      const dateInput = document.createElement("input");
      dateInput.className = "field-input";
      dateInput.type = "date";
      dateInput.value = todayInZone(new Date(), settings.businessDayTimezone);
      dateInput.dataset["testid"] = "admin-location-tax-date";
      dateLabel.append(dateInput);
      const note = document.createElement("p");
      note.className = "bd-help";
      note.textContent = "This records one combined rate. Confirm the new rate after saving it.";
      const save = document.createElement("button");
      save.type = "button";
      save.className = "btn-secondary btn-compact";
      save.textContent = "Save tax rate for this location";
      save.dataset["testid"] = "admin-location-tax-save";
      save.addEventListener("click", async () => {
        let basisPoints: number;
        try {
          basisPoints = exactHundredths(rateInput.value, "Sales tax rate");
        } catch (error) {
          statusEl.textContent = error instanceof Error ? error.message : "Enter a valid tax rate.";
          return;
        }
        save.disabled = true;
        const saved = await putTaxRates(session.apiBaseUrl, session.tenantId, {
          locationId,
          effectiveFrom: dateInput.value,
          components: [{ component: "unsplit", basisPoints }],
          idempotencyKey: crypto.randomUUID(),
        });
        if (gen !== generation) return;
        if (saved.status !== 201 && saved.status !== 200) {
          save.disabled = false;
          statusEl.textContent = `Could not save tax rate. ${errorMessage(saved.body)}`;
          return;
        }
        statusEl.textContent = "Tax rate saved. Confirm it before live card charges.";
        await loadLocation();
      });
      card.append(rateLabel, dateLabel, note, save);
    }
    if (!facts.inherited) {
      const confirm = document.createElement("button");
      confirm.type = "button";
      confirm.className = "btn-secondary btn-compact";
      confirm.textContent =
        kind === "tax_rate" ? "Confirm this tax rate" : "Confirm this time zone and cutoff";
      confirm.disabled = facts.confirmed;
      confirm.dataset["testid"] = `admin-location-confirm-${kind}`;
      confirm.addEventListener("click", async () => {
        confirm.disabled = true;
        const saved = await postLocationMoneyConfirmation(
          session.apiBaseUrl,
          session.tenantId,
          locationId,
          kind,
        );
        if (gen !== generation) return;
        if (saved.status !== 201 && saved.status !== 200) {
          confirm.disabled = false;
          statusEl.textContent = `Could not confirm this location. ${errorMessage(saved.body)}`;
          return;
        }
        statusEl.textContent =
          kind === "tax_rate"
            ? "Tax rate confirmed for this location."
            : "Business day confirmed for this location.";
        await loadLocation();
      });
      card.append(confirm);
    }
    fieldsEl.append(card);
  }
  if (hasPermission(session, "admin.tax_rate.manage")) {
    const history = await getTaxRates(session.apiBaseUrl, session.tenantId, locationId);
    if (gen !== generation || locationId !== locationSelectEl.value || history.status !== 200)
      return;
    const today = todayInZone(new Date(), settings.businessDayTimezone);
    const newestForDate = new Map<string, number>();
    for (const set of history.body.sets) {
      newestForDate.set(
        set.effectiveFrom,
        Math.max(newestForDate.get(set.effectiveFrom) ?? 0, set.rateSetNo),
      );
    }
    for (const set of history.body.sets.filter(
      (entry) =>
        entry.effectiveFrom > today && newestForDate.get(entry.effectiveFrom) === entry.rateSetNo,
    )) {
      const future = await getLocationMoneyConfirmation(
        session.apiBaseUrl,
        session.tenantId,
        locationId,
        "tax_rate",
        set.rateSetId,
      );
      if (gen !== generation || locationId !== locationSelectEl.value) return;
      if (future.status !== 200) continue;
      const card = document.createElement("div");
      card.className = "bd-card";
      const detail = document.createElement("p");
      detail.className = "bd-help";
      detail.textContent = `Scheduled sales tax: ${percent(set.totalBasisPoints)}% from ${usDate(set.effectiveFrom)}. ${future.body.confirmed ? "Confirmed." : "Confirm before it takes effect to keep live card charges available."}`;
      const confirm = document.createElement("button");
      confirm.type = "button";
      confirm.className = "btn-secondary btn-compact";
      confirm.textContent = "Confirm scheduled tax rate";
      confirm.disabled = future.body.confirmed;
      confirm.dataset["testid"] = `admin-location-confirm-scheduled-tax-${set.rateSetId}`;
      confirm.addEventListener("click", async () => {
        confirm.disabled = true;
        const saved = await postLocationMoneyConfirmation(
          session.apiBaseUrl,
          session.tenantId,
          locationId,
          "tax_rate",
          set.rateSetId,
        );
        if (gen !== generation) return;
        if (saved.status !== 201 && saved.status !== 200) {
          confirm.disabled = false;
          statusEl.textContent = `Could not confirm scheduled rate. ${errorMessage(saved.body)}`;
          return;
        }
        statusEl.textContent = "Scheduled tax rate confirmed for this location.";
        await loadLocation();
      });
      card.append(detail, confirm);
      fieldsEl.append(card);
    }
  }
}

async function loadLocation(): Promise<void> {
  const session = activeSession;
  const locationId = locationSelectEl.value;
  if (session === undefined || locationId === "") return;
  const gen = ++generation;
  currentSettings = undefined;
  fieldsEl.replaceChildren();
  statusEl.textContent = "Loading location settings.";
  const result = await getLocationSettings(session.apiBaseUrl, session.tenantId, locationId);
  if (gen !== generation) return;
  if (result.status !== 200) {
    statusEl.textContent = `Could not load location settings. ${errorMessage(result.body)}`;
    return;
  }
  statusEl.textContent = "";
  render(result.body.settings, gen);
}

async function write(
  key: LocationSettingKey,
  input: HTMLInputElement | HTMLSelectElement,
  revert: boolean,
): Promise<void> {
  const session = activeSession;
  const locationId = locationSelectEl.value;
  if (session === undefined || locationId === "") return;
  let value: unknown;
  try {
    value = revert ? undefined : readValue(key, input);
  } catch (error) {
    statusEl.textContent = error instanceof Error ? error.message : "Enter a valid value.";
    return;
  }
  const gen = ++generation;
  fieldsEl
    .querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(
      "input, select, button",
    )
    .forEach((control) => {
      control.disabled = true;
    });
  statusEl.textContent = revert ? "Reverting location setting." : "Saving location override.";
  const result = revert
    ? await revertLocationSetting(session.apiBaseUrl, session.tenantId, locationId, key)
    : await putLocationSetting(session.apiBaseUrl, session.tenantId, locationId, key, value);
  if (gen !== generation) return;
  if (result.status !== 200) {
    if (currentSettings !== undefined) render(currentSettings, gen);
    statusEl.textContent = `Could not save location setting. ${errorMessage(result.body)}`;
    return;
  }
  render(result.body.settings, gen);
  statusEl.textContent = revert
    ? "Customer value restored for this location."
    : "Override saved for this location.";
}

locationSelectEl.addEventListener("change", () => void loadLocation());

export async function loadLocationSettingsPanel(session: Session): Promise<void> {
  const gen = ++generation;
  activeSession = session;
  panelEl.hidden = true;
  fieldsEl.replaceChildren();
  const result = await getPlanningLocations(session.apiBaseUrl, session.tenantId);
  if (gen !== generation) return;
  if (result.status !== 200) return;
  locations = result.body.locations;
  if (locations.length < 2) return;
  panelEl.hidden = false;
  locationSelectEl.replaceChildren();
  for (const siteLocation of locations) {
    const option = document.createElement("option");
    option.value = siteLocation.id;
    option.textContent = siteLocation.name;
    locationSelectEl.append(option);
  }
  await loadLocation();
}

export function resetLocationSettingsPanel(): void {
  generation += 1;
  activeSession = undefined;
  locations = [];
  currentSettings = undefined;
  panelEl.hidden = true;
  fieldsEl.replaceChildren();
  locationSelectEl.replaceChildren();
  statusEl.textContent = "";
}
